/**
 * ChatExportCard — T-31 generate_export tool result.
 *
 * Renders one of five visual states based on the backend response:
 *   - Pro-locked            : free user, paywall + upgrade CTA
 *   - No analyzed term sheet: pro user but the deal has no extraction
 *   - Service unavailable   : Redis / WeasyPrint outage
 *   - Success               : download button + filename + expiry hint
 *   - First-export modal    : GC confidentiality acknowledgment before
 *                             the first download (one-time per user)
 *
 * The card never rounds corners — design system bans border-radius
 * everywhere except avatars (frontend/CLAUDE.md §Design System).
 */
import { useState } from 'react';
import { Download, FileText, Lock } from 'react-feather';

import api from '../../../api/client';
import { useCheckoutStore } from '../../../stores/checkoutStore';
import { Button } from '../../../components/ui';
import type { ToolResultProps } from '../lib/component-registry';

interface ExportResult {
  export_available: boolean;
  is_pro_required?: boolean;
  download_url?: string;
  filename?: string;
  expires_at?: string;
  needs_acknowledgment?: boolean;
  reason?: string;
  message?: string;
  export_id?: string;
}

const REASON_COPY: Record<string, string> = {
  no_analyzed_term_sheet:
    'No analyzed term sheet found for this deal. Upload one to enable export.',
  service_unavailable:
    'Export service is temporarily unavailable. Try again in a moment.',
  render_failed:
    'PDF generation failed. Please try again in a moment.',
};

export function ChatExportCard({ result }: ToolResultProps) {
  const r = (result ?? {}) as Partial<ExportResult>;
  const [showAckModal, setShowAckModal] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const openCheckout = useCheckoutStore((s) => s.openCheckout);
  const redirecting = useCheckoutStore((s) => s.redirecting);

  // ---------- Pro paywall ----------
  if (r.export_available === false && r.is_pro_required) {
    return (
      <div className="mt-2 flex items-center gap-3 border border-border bg-surface-card px-4 py-3">
        <Lock size={18} className="flex-shrink-0 text-txt-tertiary" />
        <div className="min-w-0 flex-1">
          <p className="text-body-sm font-semibold text-txt-primary">
            PDF export is a Pro feature
          </p>
          <p className="text-caption text-txt-secondary">
            Unlock downloadable analysis briefs to share with your accountant.
          </p>
        </div>
        <Button
          variant="upgrade"
          size="sm"
          onClick={() => openCheckout('monthly')}
          isLoading={redirecting}
        >
          Upgrade
        </Button>
      </div>
    );
  }

  // ---------- Generic unavailable ----------
  if (r.export_available === false) {
    const copy = (r.reason && REASON_COPY[r.reason]) || r.message
      || 'Export is unavailable. Please try again.';
    return (
      <div className="mt-2 border border-border bg-surface-subtle px-4 py-3 text-body-sm text-txt-secondary">
        {copy}
      </div>
    );
  }

  // ---------- Success ----------
  if (!r.download_url || !r.filename) {return null;}

  const triggerDownload = () => {
    if (!r.download_url) {return;}
    window.open(r.download_url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadClick = async () => {
    if (r.needs_acknowledgment && !acknowledged) {
      setShowAckModal(true);
      return;
    }
    triggerDownload();
  };

  const handleAcknowledge = async () => {
    setBusy(true);
    try {
      await api.exports.acknowledge();
    } catch {
      // Non-fatal — proceed with download regardless. The server is
      // idempotent so we can retry on the next download.
    }
    setAcknowledged(true);
    setShowAckModal(false);
    setBusy(false);
    triggerDownload();
  };

  return (
    <>
      <div className="mt-2 flex items-center gap-3 border border-border bg-surface-card px-4 py-3">
        <FileText size={18} className="flex-shrink-0 text-orange" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm font-semibold text-txt-primary">
            {r.filename}
          </p>
          <p className="text-caption text-txt-tertiary">
            PDF · expires in 1 hour
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Download size={14} />}
          onClick={handleDownloadClick}
        >
          Download
        </Button>
      </div>

      {showAckModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-ack-title"
        >
          <div className="w-full max-w-md border border-border bg-surface-card p-6 shadow-lg">
            <h3
              id="export-ack-title"
              className="text-h3 font-semibold text-txt-primary"
            >
              Before you download
            </h3>
            <p className="mt-3 text-body-sm text-txt-secondary">
              This export is for your personal use. You are responsible for
              maintaining the confidentiality of this document when sharing
              it with third parties.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setShowAckModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAcknowledge}
                isLoading={busy}
              >
                I understand — download
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
